import { Autocomplete, Container, FormControl, FormHelperText, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import FaltaReceberComponent from 'components/FaltaReceberComponent';
import dayjs from 'dayjs';
import { useSnackbarCustom } from 'hooks/useSnackCustom';
import { IAbstractModel } from 'model/abstract.model';
import { ICondicaoPagamento } from 'pages/cadastro-pessoa/pessoa.model';
import { IInfoAdicional, TdadosGeneric, TipoInformacaoAdicional } from 'pages/pagamento/pagamento.model';
import PagamentoService from 'pages/pagamento/pagamento.service';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from 'redux/hooks';
import { deepClone, roundHalfToEven } from 'utils/util';
import { FormaPagamentoDialogContentProps } from '../formapagamento.model';
import LinhasDadosGeneric from '../LinhasDadosGeneric';
import { theme } from 'theme';

const GenericOutrasFormasPagamento = (props: FormaPagamentoDialogContentProps & { tipo: TipoInformacaoAdicional; }) => {
    const { dadosPagamento, setDadosPagamento } = props.valorRecebido;
    const { faltaReceber } = useAppSelector(s => s.pagamento);
    const { hasError, setHasError } = props.error;
    const dadosPagamentoAsInfoAdicional = dadosPagamento.infoAdicional as IInfoAdicional;
    const { addError } = useSnackbarCustom();
    const { tableRows } = useAppSelector(e => e.pagamento);
    const condicoesPagamento = useAppSelector(e => e.preferencias.condicoesPagamento);
    const [filtroCondicaoPagamento, setFiltroCondicaoPagamento] = useState('');
    const [condicaoPagamentoSelecionada, setCondicaoPagamentoSelecionada] = useState<IAbstractModel | null>(!props.isNew ? dadosPagamentoAsInfoAdicional?.condicaoPagamento ?? null : null);
    const service = PagamentoService();
    const shouldRedistribuirParcelas = useRef(props.isNew || !dadosPagamentoAsInfoAdicional?.condicaoPagamento);


    const filterCondicoesPagamentos = useCallback((arr: IAbstractModel[], filtro: string) => {
        if (!filtro) return arr;
        return arr.filter(e => e.descritivo.toUpperCase().includes(filtro.toUpperCase()));
    }, []);

    const getValorTotalParcelas = (parcelas: TdadosGeneric[] | undefined) => {
        if (!parcelas?.length) return 0;
        return roundHalfToEven(parcelas?.reduce((acc, curr) => acc + curr.valor, 0) ?? 0, 2);
    };

    useEffect(() => {
        if (!shouldRedistribuirParcelas.current) return;
        shouldRedistribuirParcelas.current = false;

        if (props.uuid && condicoesPagamento && condicaoPagamentoSelecionada) {
            const parcelas = service.getParcelasCondicaoPagamentoSelecionada(props.uuid, condicaoPagamentoSelecionada.uuid, (props.customFaltaReceber ?? faltaReceber) - tableRowsValue);
            parcelas.then(response => {
                handleTableRow(response.data.length, response.data);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [condicaoPagamentoSelecionada]);

    const tableRowsValue = useMemo(() => {
        return tableRows.filter(e => !e.pago).reduce((_, act) => _ += act.valor, 0);
    }, [tableRows]);

    useEffect(() => {
        if (dadosPagamento.infoAdicional?.tipo && dadosPagamento.infoAdicional.tipo !== props.tipo) {
            setDadosPagamento({
                valorRecebido: faltaReceber,
                infoAdicional: undefined
            });
        }

        if (props.isNew) {
            if (!condicoesPagamento?.length) {
                setDadosPagamento(prev => {
                    return ({
                        ...prev,
                        valorRecebido: faltaReceber - tableRowsValue,
                        infoAdicional: {
                            descritivo: '',
                            numeroParcelas: 1,
                            tipo: props.tipo,
                            dadosGeneric: [{
                                valor: faltaReceber - tableRowsValue,
                                vencimento: "",
                                vencimentoDisplay: ""
                            }],
                            observacao: null
                        },
                    });
                });
            } else {
                setDadosPagamento(prev => {
                    return ({
                        ...prev,
                        valorRecebido: 0,
                        infoAdicional: {
                            descritivo: '',
                            numeroParcelas: 1,
                            tipo: props.tipo,
                            dadosGeneric: [],
                            observacao: null
                        },
                    });
                });
            }
        } else {
            const normalizarDadosGeneric = (items: TdadosGeneric[] | undefined): TdadosGeneric[] =>
                (items ?? []).map(g => ({
                    ...g,
                    vencimentoDisplay: g.vencimentoDisplay || g.vencimento,
                }));

            if (!condicoesPagamento || condicoesPagamento.length === 0) {
                setDadosPagamento(prev => ({
                    ...prev,
                    infoAdicional: prev.infoAdicional
                        ? { ...prev.infoAdicional, dadosGeneric: normalizarDadosGeneric(prev.infoAdicional.dadosGeneric) }
                        : prev.infoAdicional,
                }));
            } else {
                setDadosPagamento(prev => {
                    const dadosGeneric = normalizarDadosGeneric(prev.infoAdicional?.dadosGeneric);
                    return {
                        ...prev,
                        valorRecebido: getValorTotalParcelas(dadosGeneric),
                        infoAdicional: {
                            ...prev.infoAdicional!,
                            descritivo: prev.infoAdicional?.descritivo ?? '',
                            numeroParcelas: prev.infoAdicional?.numeroParcelas ?? 1,
                            dadosGeneric,
                        }
                    };
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isNew]);

    const rateiaFaltaReceberEntreParcelas = (numeroParcela: number) => {
        if (dadosPagamento.infoAdicional) {
            const faltaReceberCentavos = faltaReceber * 100;
            const sobraCentavos = faltaReceberCentavos % numeroParcela;
            const valorRateadoCentavos = (faltaReceberCentavos - sobraCentavos) / numeroParcela;
            const ultimaParcelaCentavos = valorRateadoCentavos + sobraCentavos;
            const updatedDadosGeneric = dadosPagamento.infoAdicional.dadosGeneric!;

            for (let index = 0; index < numeroParcela; index++) {
                const isUltimaParcela = index === numeroParcela - 1;
                updatedDadosGeneric[index] = {
                    valor: (isUltimaParcela ? ultimaParcelaCentavos : valorRateadoCentavos) / 100,
                    vencimento: updatedDadosGeneric[index]?.vencimento ?? "",
                    vencimentoDisplay: updatedDadosGeneric[index]?.vencimentoDisplay ?? ""
                };
            }

            const updatedInfoAdicional = { ...dadosPagamento.infoAdicional, dadosGeneric: updatedDadosGeneric, };

            setDadosPagamento((prevDadosPagamento) => ({
                ...prevDadosPagamento,
                infoAdicional: updatedInfoAdicional,
            }));
        }
    };

    const handleChangeParcela = (value: number) => {
        if (value < 0 || value === 0 || Number.isNaN(value)) return;
        if (value > 12) {
            addError({ message: "O máximo de parcelas permitido são 12", closeable: true, id: "maxParcelasGenericFormasPagamentoError" });
            rateiaFaltaReceberEntreParcelas(12);
            return setDadosPagamento(prev => ({
                ...prev, infoAdicional: {
                    descritivo: "",
                    numeroParcelas: 12,
                    tipo: props.tipo,
                    dadosGeneric: prev.infoAdicional?.dadosGeneric,
                    observacao: prev.infoAdicional?.observacao
                }
            }));
        }
        rateiaFaltaReceberEntreParcelas(value);

        setDadosPagamento(prev => {
            return ({
                ...prev, infoAdicional: {
                    descritivo: '',
                    numeroParcelas: value,
                    tipo: props.tipo,
                    dadosGeneric: prev.infoAdicional?.dadosGeneric,
                    observacao: prev.infoAdicional?.observacao
                },
            });
        });
    };

    const handleInfoAdicionalObservacao = (observacao: string) => {
        if ((dadosPagamentoAsInfoAdicional)) {
            setDadosPagamento(prevDadosPagamento => ({
                ...prevDadosPagamento,
                infoAdicional: {
                    ...prevDadosPagamento.infoAdicional!,
                    observacao
                },
            }));
        }
    };

    const handleChangeValor = (index: number, valor: number = 0) => {
        setDadosPagamento(prev => {
            if (!prev.infoAdicional) return prev;
            const dadosGeneric = deepClone(prev.infoAdicional.dadosGeneric);
            const abc = dadosGeneric?.[index];
            if (abc) {
                abc.valor = valor;
            }
            const novoValorTotal = getValorTotalParcelas(dadosGeneric);
            setHasError(() => novoValorTotal > faltaReceber);
            shouldRedistribuirParcelas.current = false;
            return {
                ...prev,
                valorRecebido: novoValorTotal,
                infoAdicional: {
                    ...prev.infoAdicional!,
                    dadosGeneric: dadosGeneric ?? [],
                },
            };
        });
    };

    const somaGeneric = (infos: IInfoAdicional) => {
        let soma = 0;
        infos.dadosGeneric?.forEach((dado: TdadosGeneric) => {
            soma += dado.valor;
        });
        return soma;
    };

    const calcularNovaData = (dataAntiga: dayjs.Dayjs | Date) => {
        return dayjs(dataAntiga).add(1, 'month').toDate();
    };

    const handleTableRow = (numeroParcela: number, dadosParcela?: TdadosGeneric[]) => {
        const dadosGeneric = Array.from((dadosPagamentoAsInfoAdicional).dadosGeneric ?? []);

        if (dadosGeneric.length > numeroParcela && numeroParcela > 0) {
            const parcelasExtra = dadosGeneric.length - numeroParcela;
            for (let i = 0; i < parcelasExtra; i++) {
                dadosGeneric.pop();
            }
        }

        if (condicoesPagamento && condicoesPagamento.length > 0 && !condicaoPagamentoSelecionada) {
            return setDadosPagamento({
                valorRecebido: faltaReceber,
                infoAdicional: {
                    numeroParcelas: 0,
                    descritivo: '',
                    tipo: props.tipo,
                    dadosGeneric: [{
                        valor: faltaReceber - tableRowsValue,
                        vencimento: "",
                        vencimentoDisplay: ""
                    }],
                }
            });
        }

        if ((!condicoesPagamento || condicoesPagamento.length === 0)) {
            for (let i = dadosGeneric.length; i < numeroParcela; i++) {
                dadosGeneric.push({
                    valor: faltaReceber,
                    vencimento: "",
                    vencimentoDisplay: ""
                });
            }
        } else if (dadosParcela) {
            for (const parcela of dadosParcela) {
                const date = new Date(parcela.vencimento);

                dadosGeneric.push({
                    valor: parcela.valor,
                    vencimento: date.toISOString(),
                    vencimentoDisplay: date.toISOString()
                });
            }
        }

        setDadosPagamento(prevDadosPagamento => ({
            ...prevDadosPagamento,
            infoAdicional: {
                ...prevDadosPagamento.infoAdicional!,
                descritivo: '',
                dadosGeneric: dadosGeneric,
                numeroParcelas: numeroParcela,
                observacao: prevDadosPagamento.infoAdicional?.observacao ?? null
            },
        }));
    };

    useEffect(() => {
        if (dadosPagamento.infoAdicional?.dadosGeneric?.length) {
            const total = somaGeneric((dadosPagamentoAsInfoAdicional));
            setDadosPagamento(prev => ({ ...prev, valorRecebido: total }));
            faltaReceber - roundHalfToEven(total, 2) >= 0 ? setHasError(false) : setHasError(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(dadosPagamentoAsInfoAdicional)?.dadosGeneric]);

    useEffect(() => {
        if (dadosPagamento.infoAdicional?.dadosGeneric?.length) {
            handleTableRow((dadosPagamentoAsInfoAdicional)?.numeroParcelas ?? 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(dadosPagamentoAsInfoAdicional)?.numeroParcelas]);


    const handleDatavencimento = (index: number, vencimentoDisplay: string) => {
        if ((dadosPagamentoAsInfoAdicional)) {
            const novosGeneric = [...(dadosPagamentoAsInfoAdicional).dadosGeneric!];
            if (index === 0) {
                novosGeneric[0] = { ...novosGeneric[0], vencimentoDisplay: vencimentoDisplay };
                let primeiraData = calcularNovaData(dayjs(vencimentoDisplay));

                for (let i = 1; i < novosGeneric.length; i++) {
                    novosGeneric[i] = { ...novosGeneric[i], vencimentoDisplay: primeiraData.toString() };
                    primeiraData = calcularNovaData(primeiraData);
                }
            } else {
                const vencimento = new Date(vencimentoDisplay).toISOString();
                novosGeneric[index] = { ...novosGeneric[index], vencimento, vencimentoDisplay };
            }

            for (let i = 0; i < novosGeneric.length; i++) {
                const dados = novosGeneric[i];
                if (dayjs(dados.vencimentoDisplay).isValid()) {
                    const vencimento = new Date(dados.vencimentoDisplay).toISOString();
                    novosGeneric[i] = { ...novosGeneric[i], vencimento };
                }
            }

            setDadosPagamento((prevDadosPagamento) => ({
                ...prevDadosPagamento,
                infoAdicional: {
                    ...prevDadosPagamento.infoAdicional!,
                    dadosGeneric: novosGeneric,
                },
            }));
        }
    };

    return (
        <>
            <FormControl
                sx={{ width: condicoesPagamento && condicoesPagamento.length > 0 ? '75%' : '5rem' }}
            >
                {
                    (!condicoesPagamento || condicoesPagamento.length === 0) &&
                    <TextField
                        variant="standard"
                        inputProps={{
                            style: {
                                textAlign: 'center',
                                fontSize: '24px',
                                fontWeight: 700,
                                fontFamily: 'Work Sans, sans-serif'
                            },
                        }}
                        id={'parcelas'}
                        name='parcelas'
                        label="Parcelas"
                        type='number'
                        inputMode='numeric'
                        value={(dadosPagamentoAsInfoAdicional)?.numeroParcelas ?? 1}
                        onChange={e => { handleChangeParcela(parseInt(e.target.value)); }}
                    />
                }
                {condicoesPagamento && condicoesPagamento.length > 0 &&
                    <Autocomplete
                        value={dadosPagamento.infoAdicional?.condicaoPagamento}
                        id="ac-condicao-pagamento-outros"
                        options={filterCondicoesPagamentos(condicoesPagamento, filtroCondicaoPagamento)}
                        getOptionLabel={(option) => option.descritivo}
                        onInputChange={(_, value) => { setFiltroCondicaoPagamento(value); }}
                        filterOptions={(x) => x}
                        noOptionsText={'Nenhum resultado encontrado'}
                        onChange={(_, condicao) => {
                            shouldRedistribuirParcelas.current = true;
                            setCondicaoPagamentoSelecionada(condicao);
                            setDadosPagamento((prevDadosPagamento) => ({
                                ...prevDadosPagamento,
                                infoAdicional: {
                                    ...prevDadosPagamento.infoAdicional!,
                                    condicaoPagamento: condicao,
                                    dadosGeneric: [],
                                    numeroParcelas: condicao ? (condicao as ICondicaoPagamento).numeroParcelas : 1,
                                    observacao: prevDadosPagamento.infoAdicional?.observacao ?? null,
                                    tipo: props.tipo,
                                },
                            }));
                        }}
                        isOptionEqualToValue={(option, value) => {
                            if (!option || !value) return false;
                            return option.uuid === value.uuid;
                        }}
                        renderInput={
                            (params) => <TextField
                                {...params}
                                label="Condição de pagamento *"
                                variant="standard"
                            />}
                    />}
            </FormControl>

            <Grid container >
                <TableContainer
                    sx={{
                        backgroundColor: theme.palette.background.default,
                        borderRadius: 1,
                        position: 'relative',
                        boxSizing: 'border-box',
                        height: '100%',
                        maxHeight: '320px',
                        minHeight: '320px',
                    }}
                    className="color-orange"
                >
                    <Table
                        id="content-table"
                        stickyHeader
                        sx={{
                            borderRadius: 1,
                        }}
                        size="small"
                    >

                        <TableHead>
                            <TableRow >
                                <TableCell sx={{ width: '3rem' }} align="center">
                                    Valor
                                </TableCell>

                                <TableCell sx={{ width: '3rem' }} align="center">
                                    Vencimento
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {dadosPagamento.infoAdicional && (dadosPagamentoAsInfoAdicional).dadosGeneric?.map((row, index) => (
                                <LinhasDadosGeneric
                                    row={row}
                                    index={index}
                                    handleChangeValor={handleChangeValor}
                                    handleDatavencimento={handleDatavencimento}
                                    hasError={hasError}
                                    key={index}
                                    possuiCondicaoPagamento={!!(condicoesPagamento && condicoesPagamento.length > 0)}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Container>
                    {hasError && <FormHelperText sx={{ textAlign: 'center', fontWeight: 600, fontSize: 15, color: theme.palette.error.main }}>O valor não pode ser maior que a venda :(</FormHelperText>}
                    {!hasError && <FormHelperText sx={{ textAlign: 'center', fontWeight: 600, fontSize: 15 }}> </FormHelperText>}
                </Container>
            </Grid>

            <TextField
                label="Observação"
                multiline
                fullWidth
                value={dadosPagamento.infoAdicional?.observacao ?? ""}
                id="boleto-dadospagamento-infoadicional-observacao"
                onChange={(e) => handleInfoAdicionalObservacao(e.target.value)}
                inputProps={{ maxLength: 200 }}
                color='primary'
                focused
            />

            <FaltaReceberComponent
                valorRecebido={dadosPagamento.valorRecebido}
                tableRowsValue={tableRowsValue}
                isAbsolute
            />
        </>
    );
};

export default GenericOutrasFormasPagamento;
